import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { ICase, IWinSkin } from '../interfaces/cases.interface';
import { Skin } from '@core/interfaces/skin.interface';
import { CasesService } from '../services/cases.service';
import { sleep, sleepWithId } from '@core/utils/sleep.util';
import { generateSeededRandomNumber } from '@core/utils/seeded-random.util';
import { BalanceIconComponent } from '../../../core/components/balance-icon/balance-icon.component';
import { DecimalPipe } from '@angular/common';
import { transition, animate, style, trigger } from '@angular/animations';
import { SvgIconComponent } from 'angular-svg-icon';
import { BalanceType } from '@core/types/balance.type';
import { SoundsService } from '@core/services/sounds.service';

@Component({
  selector: 'app-case-spinner',
  templateUrl: './case-spinner.component.html',
  styleUrls: ['./case-spinner.component.scss'],
  standalone: true,
  imports: [BalanceIconComponent, DecimalPipe, SvgIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('scaleIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.5)' }),
        animate(
          '0.2s ease-in-out',
          style({ opacity: 1, transform: 'scale(1)' })
        ),
      ]),
    ]),
  ],
})
export class CaseSpinnerComponent {
  private readonly destroyRef = inject(DestroyRef);

  accentColor = input<string>('var(--yellow)');
  currentCase = input.required<ICase>();
  isFastSpin = input<boolean>();
  playSound = input<boolean>();
  balanceType = input<BalanceType>();
  winningItemIdx = Math.floor(Math.random() * 10) + 55;

  spinnerItems = signal<Skin[]>([]);
  showResults = signal(false);

  settings = computed(() => {
    return this.casesService.CASES_SETTINGS[
      this.isFastSpin() ? 'fast' : 'regular'
    ];
  });
  wonIndex = signal<number>(0);

  @ViewChild('roll') rollElement!: ElementRef;
  @ViewChild('container') containerElement!: ElementRef;

  private animationFrameId: number | null = null;
  timeoutIds = signal<any[]>([]);

  constructor(
    private soundsService: SoundsService,
    private casesService: CasesService
  ) {
    effect(() => {
      this.randomizeSpinnerItems();
    });

    this.destroyRef.onDestroy(() => {
      this.timeoutIds().forEach((id) => clearTimeout(id));
    });
  }

  ngOnDestroy() {
    this.stopAnimation();
  }

  private applyWinItem(winItem: IWinSkin) {
    this.spinnerItems.update((items) => {
      return items.map((item, index) => {
        if (index === this.settings().WIN_INDEX) {
          const winSkin = this.currentCase().skins.find((item) => {
            return item.name.toLowerCase() === winItem.skin.toLowerCase();
          });

          if (winSkin) {
            return winSkin;
          }
        }
        return item;
      });
    });
  }

  private resetRoll(sizeCoefficient: number) {
    this.showResults.set(false);
    this.rollElement.nativeElement.style.transition = `none`;
    this.rollElement.nativeElement.style.transform = `translateY(-${
      sizeCoefficient * 5 + sizeCoefficient / 2 + 0.1
    }%)`;
    this.rollElement.nativeElement
      .querySelectorAll('.win')
      .forEach((el: any) => el.classList.remove('win'));
    this.rollElement.nativeElement
      .querySelectorAll('.lose')
      .forEach((el: any) => el.classList.remove('lose'));

    this.randomizeSpinnerItems();
  }

  private randomizeSpinnerItems() {
    const items: Skin[] = [];
    while (items.length < 100) {
      const itemModifiedColor =
        this.currentCase().skins[
          Math.floor(Math.random() * this.currentCase().skins.length)
        ];
      items.push(itemModifiedColor);
    }

    this.spinnerItems.set(items);
  }

  async roll(winItem: IWinSkin, allWinItems: IWinSkin[]) {
    const {
      ITEMS_QUANTITY,
      ROLLING_CLOSER_TIMEOUT,
      ROLLING_TIMEOUT,
      WIN_INDEX,
    } = this.settings();
    this.wonIndex.set(WIN_INDEX);
    const sizeCoefficient = 100 / ITEMS_QUANTITY;

    this.resetRoll(sizeCoefficient);
    this.applyWinItem(winItem);

    await sleep(100);

    const GAP_SIZE_PERCENTAGE = 0.01;
    const endPositionPercentage =
      (await generateSeededRandomNumber(
        `${winItem.chance}chance${winItem.multiplier}`,
        0,
        100
      ).then((result) => result)) / 100;

    const middleLocation = -(sizeCoefficient * WIN_INDEX) - sizeCoefficient / 2;
    const transformX =
      -(sizeCoefficient * WIN_INDEX) -
      (sizeCoefficient - GAP_SIZE_PERCENTAGE * 2) * endPositionPercentage -
      GAP_SIZE_PERCENTAGE;
    this.rollElement.nativeElement.style.left = `50%`;

    this.rollElement.nativeElement.style.transition = `transform ${
      ROLLING_TIMEOUT / 1000
    }s cubic-bezier(0, 0.75, 0, 1)`;
    this.rollElement.nativeElement.style.transform = `translateX(${transformX}%)`;

    this.startAnimation(sizeCoefficient);

    const { promise: p1, timeoutId: t1 } = await sleepWithId(ROLLING_TIMEOUT);
    this.timeoutIds.update((ids) => [...ids, t1]);
    await p1;
    this.timeoutIds.update((ids) => ids.filter((id) => id !== t1));

    this.stopAnimation();
    if (this.playSound()) {
      this.soundsService.playSound('tick', {
        extension: 'mp3',
        maxVolume: 0.2,
      });
    }

    this.showResults.set(true);
    this.toggleStyles('add');

    this.rollElement.nativeElement.style.transition = `transform 0.3s ease-in-out`;
    this.rollElement.nativeElement.style.transform = `translateX(${middleLocation}%)`;

    if (this.playSound()) {
      const prices = allWinItems.map((item) => item.price);
      const highestPrice = prices.reduce((a, b) => (a > b ? a : b), 0);
      const multiplier = highestPrice / this.currentCase().price;

      if (multiplier > 25) {
        this.soundsService.playSound('lightning', { maxVolume: 0.1 });
      } else if (multiplier > 10) {
        this.soundsService.playSound('plinko-high', {
          maxVolume: 0.6,
          extension: 'mp3',
        });
      } else if (multiplier > 3) {
        this.soundsService.playSound('plinko-med', {
          maxVolume: 0.8,
          extension: 'mp3',
        });
      } else {
        this.soundsService.playSound('plinko-low', {
          maxVolume: 0.8,
          extension: 'mp3',
        });
      }
    }

    await sleep(ROLLING_CLOSER_TIMEOUT);
    const { promise: p2, timeoutId: t2 } = await sleepWithId(ROLLING_TIMEOUT);
    this.timeoutIds.update((ids) => [...ids, t2]);
    await p1;
    this.timeoutIds.update((ids) => ids.filter((id) => id !== t2));
  }

  toggleStyles(type: 'remove' | 'add' = 'add') {
    const children = Array.from(
      this.rollElement?.nativeElement?.children ?? []
    );
    for (let i = 0; i < children.length; i++) {
      const child: HTMLElement | null = children[i] as HTMLElement | null;

      child?.classList?.remove('win');
      child?.classList?.remove('lose');

      if (type === 'add') {
        child?.classList?.add(i === this.settings().WIN_INDEX ? 'win' : 'lose');
      }
    }
  }

  private startAnimation(size: number) {
    const animate = () => {
      const containerRect =
        this.containerElement.nativeElement.getBoundingClientRect();
      const rollRect = this.rollElement.nativeElement.getBoundingClientRect();
      const rollCenter = containerRect.left + containerRect.width / 2;

      const transformPercentage =
        (Math.abs(rollRect.left - rollCenter) / rollRect.width) * 100;
      const currentRoll = Math.floor(transformPercentage / size);

      if (
        currentRoll >= 0 &&
        currentRoll < this.rollElement.nativeElement.children.length
      ) {
        this.updateActiveItem(currentRoll);
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private stopAnimation() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private updateActiveItem(index: number) {
    const rollElement = this.rollElement.nativeElement;
    const currentActive = rollElement.querySelector('.active');
    const newActive = rollElement.children[index];

    if (currentActive !== newActive) {
      if (currentActive) {
        currentActive.classList.remove('active');
      }
      if (newActive && !newActive.classList.contains('active')) {
        newActive.classList.add('active');
        if (this.playSound()) {
          this.soundsService.playSound('tick', {
            extension: 'mp3',
            maxVolume: 0.7,
          });
        }
      }
    }
  }
}
